import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CalendarOptions, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EventService } from 'src/app/core/service/event.service';
import { EventType } from 'src/app/core/constants/events';
import { CalendarioHitosService } from './calendario-hitos.service';
import { CalendarioHito } from './calendario-hitos.model';
import { calendarioHitosTranslations } from './translations-calendario-hitos';
import { formatDateString, formatDateStringWithTime, getCurrentLanguage, loggedInUser } from '../core/helpers/utils';
import { UsuarioService } from '../usuarios/usuarios.service';
import { Usuario } from '../usuarios/usuarios.model';
import { AuthenticationService } from '../core/service/auth.service';

FullCalendarModule.registerPlugins([
  dayGridPlugin,
  interactionPlugin,
  bootstrapPlugin
]);

@Component({
  selector: 'app-calendario-hitos',
  templateUrl: './calendario-hitos.component.html',
  styleUrls: ['./calendario-hitos.component.scss']
})
export class CalendarioHitosComponent implements OnInit {
  calendarOptions: CalendarOptions = {};
  calendarEvents: EventInput[] = [];
  hitos: CalendarioHito[] = [];
  loaded: boolean = false;
  error: string = '';
  selectedDayLabel: string = '';
  selectedDayHitos: CalendarioHito[] = [];
  selectedHito: CalendarioHito | null = null;
  selectedDayGroups: { caseLabel: string; caseId?: string; hitos: CalendarioHito[] }[] = [];
  selectedFilter: string = 'mine';
  selectedCaseFilter: string = 'all';
  adminUsers: Usuario[] = [];
  currentUserId?: string;
  availableCases: { id: string; label: string; count: number }[] = [];

  currentTitle: string = '';
  currentHeaderLabel: string = '';

  private currentLanguage: string = getCurrentLanguage();
  translations = calendarioHitosTranslations[this.currentLanguage] || calendarioHitosTranslations['es'];

  @ViewChild('dayModal', { static: true }) dayModal!: TemplateRef<any>;
  @ViewChild('hitoModal', { static: true }) hitoModal!: TemplateRef<any>;
  @ViewChild('calendar') calendarComponent?: FullCalendarComponent;

  constructor(
    private calendariosHitosService: CalendarioHitosService,
    private modalService: NgbModal,
    private eventService: EventService,
    private usuarioService: UsuarioService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.eventService.broadcast(EventType.CHANGE_PAGE_TITLE, {
      title: this.translations.LABELS.CALENDARIO_TITULO,
      breadCrumbItems: [{ label: this.translations.LABELS.CALENDARIO_TITULO, path: '.', active: true }]
    });

    this.initCalendar();
    this.currentHeaderLabel = `${this.translations.LABELS.CALENDARIO_TITULO}:`;
    this.loadAdminUsers();
    this.authService.currentUser().subscribe((user) => {
      const sessionUser = loggedInUser();
      this.currentUserId = user?.id || sessionUser?.id;
      if (this.currentUserId) {
        this.adminUsers = this.adminUsers.filter((admin) => admin.id !== this.currentUserId);
      }
      this.selectedFilter = this.currentUserId ? 'mine' : 'all';
      this.fetchHitos(this.selectedFilter);
    });
  }

  private initCalendar(): void {
    const locale = this.currentLanguage === 'es' ? esLocale : 'en';
    this.calendarOptions = {
      themeSystem: 'bootstrap',
      locale,
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: '',
        center: '',
        right: ''
      },
      dayMaxEvents: 3,
      eventDisplay: 'block',
      displayEventTime: false,
      editable: false,
      selectable: false,
      events: [...this.calendarEvents],
      datesSet: this.handleDatesSet.bind(this),
      dateClick: this.handleDateClick.bind(this),
      eventClick: this.handleEventClick.bind(this),
      moreLinkText: (count: number) => `${count} ${this.translations.LABELS.MORE}`,
      moreLinkClick: (arg: any) => {
        arg.jsEvent.preventDefault();
        this.openDayModal(arg.date);
        return 'none';
      }
    };
  }

  private fetchHitos(filterValue?: string): void {
    this.loaded = false;
    this.error = '';
    const idUsuario = this.getSelectedUserId(filterValue);
    this.calendariosHitosService.getHitos(idUsuario).subscribe({
      next: (hitos) => {
        this.hitos = hitos || [];
        this.availableCases = this.getAvailableCases(this.hitos);
        if (this.selectedCaseFilter !== 'all' && !this.availableCases.some((c) => c.id === this.selectedCaseFilter)) {
          this.selectedCaseFilter = 'all';
        }
        this.calendarEvents = this.buildCalendarEvents(this.applyCaseFilter(this.hitos));
        this.calendarOptions.events = [...this.calendarEvents];
        this.loaded = true;
      },
      error: (error) => {
        this.error = error?.message || error;
        this.loaded = false;
      }
    });
  }

  private loadAdminUsers(): void {
    this.usuarioService.getUsuarios('nivel_acceso=ADMIN&activo=true').subscribe({
      next: (usuarios) => {
        const filtered = (usuarios || []).filter((user) => user.id !== this.currentUserId);
        this.adminUsers = filtered.sort((a, b) =>
          this.getAdminLabel(a).localeCompare(this.getAdminLabel(b))
        );
      },
      error: () => {
        this.adminUsers = [];
      }
    });
  }

  onFilterChange(value: string): void {
    this.selectedFilter = value;
    this.fetchHitos(value);
  }

  onCaseFilterChange(value: string): void {
    this.selectedCaseFilter = value;
    this.calendarEvents = this.buildCalendarEvents(this.applyCaseFilter(this.hitos));
    this.calendarOptions.events = [...this.calendarEvents];
  }

  private buildCalendarEvents(hitos: CalendarioHito[]): EventInput[] {
    return hitos
      .map((hito) => this.mapHitoToEvent(hito))
      .filter((event): event is EventInput => !!event);
  }

  private mapHitoToEvent(hito: CalendarioHito): EventInput | null {
    const tipo = (hito.tipo || 'HITO').toUpperCase();
    const title = this.getEventTitle(hito);

    if (tipo === 'DIA_INHABIL') {
      const startDate = this.getDatePart(hito.fecha_inicio || hito.fecha_fin);
      const endDate = this.getDatePart(hito.fecha_fin || hito.fecha_inicio);

      if (!startDate || !endDate) {
        return null;
      }

      return {
        id: hito.id,
        title,
        start: startDate,
        end: this.addDaysToDatePart(endDate, 1),
        allDay: true,
        classNames: ['calendario-hitos__event', 'calendario-hitos__event--dia-inhabil'],
        extendedProps: { hito }
      };
    }

    if (!hito.fecha_fin) {
      return null;
    }
    const hitoDatePart = this.getDatePart(hito.fecha_fin);
    if (!hitoDatePart) {
      return null;
    }

    return {
      id: hito.id,
      title,
      start: hitoDatePart,
      allDay: true,
      classNames: ['calendario-hitos__event', 'calendario-hitos__event--hito'],
      extendedProps: { hito }
    };
  }

  handleDateClick(arg: DateClickArg): void {
    this.openDayModal(arg.date);
  }

  handleDatesSet(arg: DatesSetArg): void {
    this.currentTitle = arg.view.title || '';
    this.currentHeaderLabel = `${this.translations.LABELS.CALENDARIO_TITULO}: ${this.currentTitle}`;
  }

  handleEventClick(arg: EventClickArg): void {
    arg.jsEvent.preventDefault();
    const hito = (arg.event.extendedProps as { hito?: CalendarioHito }).hito;
    if (hito) {
      this.openHitoModal(hito);
    }
  }

  openDayModal(date: Date): void {
    this.selectedDayLabel = this.formatDayLabel(date);
    this.selectedDayHitos = this.getHitosForDate(date);
    this.selectedDayGroups = this.groupHitosByCase(this.selectedDayHitos);
    this.modalService.open(this.dayModal, { size: 'md', centered: true, scrollable: true });
  }

  openHitoModal(hito: CalendarioHito): void {
    this.selectedHito = hito;
    this.modalService.open(this.hitoModal, { size: 'md', centered: true, scrollable: true });
  }

  openHitoFromDay(hito: CalendarioHito, modalRef: any): void {
    if (modalRef) {
      modalRef.close();
    }
    this.openHitoModal(hito);
  }

  getEventTitle(hito: CalendarioHito): string {
    const idExpediente = (hito.numero_expediente || hito.id_expediente || '').trim();
    const descripcion = hito.descripcion ? hito.descripcion.trim() : '';

    if (idExpediente && descripcion) {
      return `${idExpediente} - ${descripcion}`;
    }

    return idExpediente || descripcion || '';
  }

  getTipoLabel(tipo?: string): string {
    if ((tipo || '').toUpperCase() === 'DIA_INHABIL') {
      return this.translations.LABELS.DIA_INHABIL;
    }
    return this.translations.LABELS.HITO;
  }

  formatHitoFecha(hito: CalendarioHito): string {
    const tipo = (hito.tipo || 'HITO').toUpperCase();

    if (tipo === 'DIA_INHABIL') {
      const inicio = hito.fecha_inicio ? formatDateString(hito.fecha_inicio, this.currentLanguage) : '';
      const fin = hito.fecha_fin ? formatDateString(hito.fecha_fin, this.currentLanguage) : '';

      if (!inicio && !fin) {
        return '';
      }

      if (inicio && fin && inicio !== fin) {
        return `${inicio} - ${fin}`;
      }

      return inicio || fin;
    }

    return hito.fecha_fin ? formatDateStringWithTime(hito.fecha_fin, this.currentLanguage) : '';
  }

  getHitoTime(hito: CalendarioHito): string {
    if (this.isDiaInhabil(hito)) {
      return '';
    }
    if (!hito.fecha_fin) {
      return '';
    }
    const timePart = hito.fecha_fin.split('T')[1];
    if (!timePart) {
      return '';
    }
    const [hour, minute] = timePart.split(':');
    if (!hour || !minute) {
      return '';
    }
    return `${hour}:${minute}`;
  }

  getDayListLine(hito: CalendarioHito): string {
    const descripcion = hito.descripcion ? hito.descripcion.trim() : '';
    if (this.isDiaInhabil(hito)) {
      return descripcion;
    }
    const hora = this.getHitoTime(hito);
    const horaLabel = hora ? `(${hora})` : '';
    return [descripcion, horaLabel].filter(Boolean).join(' ');
  }

  getHitoModalTitle(hito?: CalendarioHito | null): string {
    if (!hito) {
      return '';
    }
    return this.formatHitoFecha(hito);
  }

  getAdminLabel(user: Usuario): string {
    const nombre = user.nombre ? user.nombre.trim() : '';
    const apellidos = user.apellidos ? user.apellidos.trim() : '';
    const label = `${nombre} ${apellidos}`.trim();
    return label || user.email || user.id || '';
  }

  getSelectedUserId(filterValue?: string): string | undefined {
    const filter = filterValue ?? this.selectedFilter;
    if (filter === 'mine') {
      return this.currentUserId;
    }
    if (filter === 'all') {
      return undefined;
    }
    if (filter.startsWith('user:')) {
      const id = filter.slice(5);
      return id ? id : undefined;
    }
    return undefined;
  }

  getCaseLabel(idExpediente?: string): string {
    return (idExpediente || '').trim();
  }

  isDiaInhabil(hito?: CalendarioHito | null): boolean {
    return (hito?.tipo || '').toUpperCase() === 'DIA_INHABIL';
  }

  private getHitosForDate(date: Date): CalendarioHito[] {
    const datePart = this.toDatePart(date);

    return this.hitos
      .filter((hito) => this.passesCaseFilter(hito))
      .filter((hito) => {
        const tipo = (hito.tipo || 'HITO').toUpperCase();
        const start = this.getDatePart(tipo === 'DIA_INHABIL' ? hito.fecha_inicio : hito.fecha_fin);
        const end = this.getDatePart(tipo === 'DIA_INHABIL' ? hito.fecha_fin : hito.fecha_fin);

        if (!start) {
          return false;
        }

        if (tipo === 'DIA_INHABIL') {
          if (!end) {
            return false;
          }
          return datePart >= start && datePart <= end;
        }

        return datePart === start;
      })
      .sort((a, b) => (a.fecha_fin || '').localeCompare(b.fecha_fin || ''));
  }

  private groupHitosByCase(hitos: CalendarioHito[]): { caseLabel: string; caseId?: string; hitos: CalendarioHito[] }[] {
    const groups = new Map<string, { caseId?: string; hitos: CalendarioHito[] }>();
    hitos.forEach((hito) => {
      const caseLabel = (hito.numero_expediente || hito.id_expediente || '').trim() || '-';
      const caseId = hito.id_expediente || undefined;
      if (!groups.has(caseLabel)) {
        groups.set(caseLabel, { caseId, hitos: [] });
      }
      groups.get(caseLabel)!.hitos.push(hito);
    });
    return Array.from(groups.entries()).map(([caseLabel, data]) => ({
      caseLabel,
      caseId: data.caseId,
      hitos: data.hitos
    }));
  }

  private formatDayLabel(date: Date): string {
    const datePart = this.toDatePart(date);
    return formatDateString(`${datePart}T00:00:00`, this.currentLanguage);
  }

  private getDatePart(dateString?: string): string | null {
    if (!dateString) {
      return null;
    }

    const parts = dateString.split('T');
    return parts.length ? parts[0] : null;
  }

  private addDaysToDatePart(datePart: string, days: number): string {
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().split('T')[0];
  }

  private toDatePart(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getAvailableCases(hitos: CalendarioHito[]): { id: string; label: string; count: number }[] {
    const counts = new Map<string, number>();
    hitos.forEach((hito) => {
      const numero = hito.numero_expediente || hito.id_expediente;
      if (numero) {
        counts.set(numero, (counts.get(numero) || 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .map(([id, count]) => ({ id, label: id, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private applyCaseFilter(hitos: CalendarioHito[]): CalendarioHito[] {
    if (this.selectedCaseFilter === 'all') {
      return hitos;
    }
    return hitos.filter((hito) => (hito.numero_expediente || hito.id_expediente) === this.selectedCaseFilter);
  }

  private passesCaseFilter(hito: CalendarioHito): boolean {
    if (this.selectedCaseFilter === 'all') {
      return true;
    }
    return (hito.numero_expediente || hito.id_expediente) === this.selectedCaseFilter;
  }

  navigatePrev(): void {
    this.calendarComponent?.getApi().prev();
  }

  navigateNext(): void {
    this.calendarComponent?.getApi().next();
  }

  navigateToday(): void {
    this.calendarComponent?.getApi().today();
  }
}
